import { Component, EventEmitter, inject, Input, Output, signal, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, forkJoin, shareReplay, } from 'rxjs';
import {
  BuildingEquipmentMergeDto,
  BuildingEquipmentService,
  BuildingItemDto,
  BuildingSectionItemDto,
  BuildingSectionService,
  CustomFieldCollectionDetailDto,
  CustomFieldCollectionService,
  EquipmentItemDto,
  EquipmentService,
} from '@artemis-software/wr-api';
import { CustomFieldCollectionInstanceDetailDto } from '@artemis-software/wr-api';
import { BuildingEquipmentDetailDto } from '@artemis-software/wr-api/model/buildingEquipmentDetailDto';
import { createForm } from '@sonofabit/ng-core';
import { FormGroup, Validators } from '@angular/forms';
import { attachmentsFromDetails, attachmentToMerge } from '@shared/form-controls/attachment-upload/attachment-utils';
import { tryWithLoading } from '@/utils/async-utils';
import { PendingChanges } from '@guards/pending-changes.guard';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { catalogTenantId } from '@/utils/admin-utils';
import { Tracked } from '@/utils/signals/tracked';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import {
  CustomFieldCollectionsComponent
} from '@shared/components/custom-field-collections/custom-field-collections.component';

export interface BuildingAndSection {
  building: BuildingItemDto;
  section?: BuildingSectionItemDto;
}

/**
 * For callers that do know the section. `section` became optional so the building dashboard can
 * preselect a building alone; annotate with this where both are known, otherwise dropping the
 * section silently compiles.
 */
export type BuildingWithSection = Required<BuildingAndSection>;

export interface BuildingEquipmentOrBuilding {
  buildingEquipment?: BuildingEquipmentDetailDto;
  building?: BuildingAndSection;
}

@Component({
  standalone: false,
  selector: 'wr-building-equipment-detail-form[buildingEquipment]',
  templateUrl: './building-equipment-detail-form.component.html',
  styleUrls: ['./building-equipment-detail-form.component.scss'],
})
export class BuildingEquipmentDetailFormComponent implements PendingChanges {
  isEditing = false;
  readonly canEdit = signal(false);

  readonly currentTenantId = catalogTenantId();

  buildingEquipmentId: string | null = null;

  readonly buildingSectionService = inject(BuildingSectionService);
  private readonly equipmentService = inject(EquipmentService);
  private readonly collectionService = inject(CustomFieldCollectionService);
  private readonly toastrService = inject(NbToastrService);
  private readonly buildingEquipmentService = inject(BuildingEquipmentService);
  private readonly dialogService = inject(NbDialogService);

  @Tracked()
  buildingId: string | undefined;

  readonly buildingSections = asyncComputed(() => {
    if (this.buildingId) {
      return this.buildingSectionService.findAllByBuildingId(this.buildingId);
    }
    return [];
  });

  //TODO WR-1566
  @Input()
  set buildingEquipment(value: BuildingEquipmentDetailDto | null | undefined) {
    if(value===undefined || typeof value === 'string') {
      return;
    }
    if(value===null) {
      // A new-entity form may already exist because `buildingAndSection` arrived first.
      // Re-initializing here would drop the preselected building and make the outcome depend on
      // the order of the two attributes in the caller's template.
      if (!this.isNewForm) {
        this.init(null);
      }
      return;
    }
    this.init({ buildingEquipment: value });
  }

  @Input()
  set buildingAndSection(value: BuildingAndSection | null | undefined) {
    if (value === undefined) {
      return;
    }
    if (this.form) {
      // Only a new-entity form may be preselected. Re-initializing an existing one would discard
      // input — and for a persisted TGA it would replace the loaded entity with an empty form,
      // reachable when a slow preselection answer arrives after a switch to `/buildingEquipment/:id`.
      if (value && this.isNewForm) {
        this.patchBuildingAndSection(this.form, value);
      }
      return;
    }
    this.init(value ? { building: value } : null);
  }

  /**
   * Derived from the form itself rather than from `isNew`: `submit()` rebuilds the form through
   * `initForm` without going via `init()`, so `isNew` stays `true` for a form that already holds a
   * persisted entity.
   */
  private get isNewForm(): boolean {
    return !!this.form && !this.form.controls.id.value;
  }

  private patchBuildingAndSection(form: NonNullable<typeof this.form>, value: BuildingAndSection): void {
    // Order matters: the building valueChanges handler clears a section not belonging to it.
    form.controls.building.setValue(value.building);
    if (value.section) {
      form.controls.buildingSectionId.setValue(value.section.id);
    }
  }

  @Input() showCard = true;
  @Input() showTitle = true;
  @Input() disableEdit = false;
  @Input() showCollections = true;
  @Input() showFooter = true;
  @Input() showCloseButton = false;
  /**
   * Bounds the card to the viewport so its body scrolls. Only correct inside an overlay — on a page
   * the card must grow with its content and scroll with the page.
   */
  @Input() inDialog = false;

  @Output()
  cancelled = new EventEmitter();
  @Output()
  submitSuccess = new EventEmitter<BuildingEquipmentDetailDto>();
  @Output()
  closed = new EventEmitter();
  @Output()
  deleteSuccess = new EventEmitter<string>();

  form?: ReturnType<typeof this.initForm>;

  isNew = false;
  firstSubmit = false;
  isSaving = false;
  loading$ = new BehaviorSubject(false);

  customFieldCollections = signal<CustomFieldCollectionDetailDto[]>([]);
  customFieldCollectionInstances = signal<CustomFieldCollectionInstanceDetailDto[] | undefined>(undefined);

  @ViewChild(CustomFieldCollectionsComponent)
  customFieldCollectionsComponent!: CustomFieldCollectionsComponent;

  get allForms(): (FormGroup | undefined)[] {
    const base: (FormGroup | undefined)[] = [this.form];
    if (this.showCollections && this.customFieldCollectionsComponent) {
      base.push(...this.customFieldCollectionsComponent.customForms.map(c => c.customForm?.form).filter(f => !!f));
    }
    return base;
  }

  private init(dto: BuildingEquipmentOrBuilding | null) {
    this.form = this.initForm(dto);
    this.isNew = !dto?.buildingEquipment?.id;
    if (!this.isNew) {
      this.form.disable();
      setTimeout(() => {
        this.disable();
      }, 100);
    }
    else {
      this.canEdit.set(true);
      this.isEditing = true;
    }
  }

  initForm(dto: BuildingEquipmentOrBuilding | null) {
    if (dto) {
      const buildingEquipment = dto.buildingEquipment;
      if (buildingEquipment) {
        this.canEdit.set(buildingEquipment.permissions.canEdit);
      }
      else {
        this.canEdit.set(true);
        this.isEditing = true;
      }
    }
    const form = createForm(({ field, mapToId }) => {
      return {
        note: field({ value: 'this is building equipment detail form' }),
        id: field({ value: dto?.buildingEquipment?.id }),
        building: field({
          value: dto?.buildingEquipment?.building || dto?.building?.building,
          validators: [Validators.required],
        }),
        buildingSectionId: field({
          value: dto?.buildingEquipment?.buildingSection?.id || dto?.building?.section?.id,
          validators: [Validators.required],
        }),
        equipment: field({
          value: dto?.buildingEquipment?.equipment,
          validators: [Validators.required],
          alias: 'equipmentId',
          toDto: entity => mapToId(entity),
        }),
        quantity: field({ value: dto?.buildingEquipment?.quantity, validators: [Validators.required] }),
        attachments: field({
          value: attachmentsFromDetails(dto?.buildingEquipment?.attachments ?? []),
          toDto: attachments => attachments?.map(dto => attachmentToMerge(dto)),
        }),
        inspectionEntryAttachments: field({
          value: attachmentsFromDetails(dto?.buildingEquipment?.inspectionEntryAttachments ?? []),
          toDto: attachments => attachments?.map(dto => attachmentToMerge(dto)),
        }),
      };
    });

    const buildingControl = form.controls.building;

    buildingControl.valueChanges.subscribe((building: BuildingItemDto | null | undefined) => {
      const currentSection = form.value.buildingSectionId;
      if (!building?.sections?.find(s => s.id === currentSection)) {
        form.controls.buildingSectionId.setValue(undefined);
      }
      this.buildingId = building?.id;
    });

    if (dto?.buildingEquipment?.building || dto?.building) {
      buildingControl.setValue(dto.buildingEquipment?.building || dto.building?.building);
    }

    if (dto?.buildingEquipment?.id) {
      form.controls.equipment.disable();
    }

    form.controls.equipment.valueChanges.subscribe(async (equipment: EquipmentItemDto | null | undefined) => {
      if (equipment) {
        const collections =
          await firstValueFrom(this.equipmentService.findEquipmentCustomFieldCollectionByEquipmentId(equipment.id));
        const sortedCollections = collections.sort((a, b) => a.ranking - b.ranking).map(c => c.collection);
        const observables = sortedCollections.map(c => this.collectionService.findById(c.id).pipe(shareReplay(1)));
        const collectionDetails = observables.length ? await firstValueFrom(forkJoin(observables)) : [];
        this.customFieldCollections.set(collectionDetails);
      }
    });

    if (dto?.buildingEquipment?.instances) {
      this.customFieldCollectionInstances.set(dto.buildingEquipment.instances);
    }

    return form;
  }


  enable(): void {
    this.allForms.forEach(f => f?.enable());
    this.isEditing = true;
  }

  disable(): void {
    this.allForms.forEach(f => f?.disable());
  }

  async submit(): Promise<boolean> {
    // Re-entry guard for the dialog path, whose own footer calls this directly: `isSaving` only
    // disables the button once change detection has run, a fast double click can beat it.
    if (this.isSaving) {
      return false;
    }
    this.firstSubmit = true;

    this.allForms.forEach(f => f?.markAllAsTouched());

    if (this.allForms.some(f => f?.invalid)) {
      this.toastrService.danger('Gebäude kann nicht gespeichert werden, es fehlen noch Daten.', 'Gebäudeausstattung');
      return false;
    }

    await tryWithLoading(this.loading$, async () => {
      this.isSaving = true;
      try {
        const mergeDto: BuildingEquipmentMergeDto = {
          ...await this.form!.getMappedValue() as BuildingEquipmentMergeDto,
          ...(this.showCollections && this.customFieldCollectionsComponent ? {
            instances: this.customFieldCollectionsComponent.customForms.map(form => form.getMergeDto()),
          } : {}),
        };
        const buildingEquipment = await firstValueFrom(this.buildingEquipmentService.merge(mergeDto));
        this.customFieldCollectionsComponent?.customForms.forEach(cf => cf.markAsPristine());
        this.form = this.initForm({ buildingEquipment });
        this.disable();
        this.toastrService.success('Gebäudeausstattung wurde erfolgreich gespeichert', 'Gebäude');
        this.submitSuccess.emit(buildingEquipment);
      } finally {
        this.isSaving = false;
      }
    });
    this.isEditing = false;
    return true;
  }

  async onDelete(): Promise<void> {
    const id = this.form!.controls.id.value;
    if (await firstValueFrom(this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Gebäudeausstattung löschen',
        message: 'Bist du sicher, dass du diese Gebäudeausstattung löschen willst?',
      },
    }).onClose)) {
      await tryWithLoading(this.loading$, async () => {
        await firstValueFrom(this.buildingEquipmentService.deleteById(id!));
        this.toastrService.success('Gebäudeausstattung wurde erfolgreich gelöscht', 'Gebäudeausstattung');
        this.deleteSuccess.emit(id!);
      });
    }
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return (this.form?.enabled ?? false) && this.allForms.some(f => f?.dirty);
  }
}
